import React from 'react';

import AssistantMessage from './assistant_message';
import LoadingSpinner from 'components/ui/loading_spinner';
import UserMessage from './user_message';

import type { Message } from '../types';

interface Props {
  between?: (turn: number) => React.ReactNode;
  // What stands in for a conversation that has not started. Surface-specific -- the assistant offers
  // its starters and a resume link, the doc editor offers example page edits -- so it is passed in
  // rather than picked here.
  empty: React.ReactNode;
  // Render assistant replies as markdown instead of sanitized HTML. The doc page editor's agent
  // answers in markdown; the other assistants answer in HTML.
  markdown?: boolean;
  messages: Message[];
  restoring: boolean;
}

export function Transcript({ between, empty, markdown = false, messages, restoring }: Props) {
  // A card is only history once the user has typed past it. An assistant message after it is the
  // reply it is still waiting on - or the error from a failed attempt, which it can be retried from.
  const lastUserIndex = messages.reduce((last, { role }, index) => (role === 'user' ? index : last), -1);

  if (messages.length === 0) {
    return <>{restoring ? <LoadingSpinner /> : empty}</>;
  }

  let turn = 0;
  let placed = -1;

  function place() {
    if (placed === turn) {
      return null;
    }

    placed = turn;
    return between?.(turn);
  }

  return (
    <>
      {messages.map((message, index) => {
        if (message.role !== 'user') {
          return (
            <React.Fragment key={index}>
              {message.local && place()}
              <AssistantMessage
                latest={index === messages.length - 1}
                markdown={markdown}
                message={message}
                superseded={index < lastUserIndex}
              />
            </React.Fragment>
          );
        }

        const before = place();
        turn += 1;
        return (
          <React.Fragment key={index}>
            {before}
            <UserMessage message={message} />
          </React.Fragment>
        );
      })}
      {place()}
    </>
  );
}
